// ===========================================
// Send Template Test Button
// ===========================================
// "Send test email" for a system email template, used by the editor (sends the unsaved draft)
// and the preview dialog (sends what is saved). The email always goes to the signed-in admin's
// own address — the endpoint has no recipient field, so a test can never reach a family.

import { useState } from 'react';
import { Alert, Button, CircularProgress, Snackbar } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useMutation } from '@tanstack/react-query';
import { sendTemplateTestEmail } from '../../../api/emailBranding.api';

interface SendTemplateTestButtonProps {
  templateKey: string;
  /** Unsaved editor fields to test instead of the saved template. Omit to test what is saved. */
  getDraft?: () => { subject?: string; preheader?: string; bodyHtml?: string };
  disabled?: boolean;
}

const FALLBACK_ERROR = 'The test email could not be sent. Please try again.';

/**
 * The server's own words. `apiClient` rejects with the raw AxiosError, whose `message` is
 * "Request failed with status code 502". The route answers `{ error }`, a thrown AppError
 * reaches the error handler as `{ message }`, and the rate limiter answers PLAIN TEXT
 * ("Too many test emails sent…"), so all three shapes are read. The editor's Save uses it too.
 */
export function testErrorMessage(error: unknown, fallback: string = FALLBACK_ERROR): string {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const { error: err, message } = data as { error?: unknown; message?: unknown };
    if (typeof err === 'string' && err) return err;
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

export default function SendTemplateTestButton({ templateKey, getDraft, disabled }: SendTemplateTestButtonProps) {
  const [result, setResult] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => sendTemplateTestEmail(templateKey, getDraft ? getDraft() : {}),
    onSuccess: (data) => setResult({ severity: 'success', message: `Test email sent to ${data.to}` }),
    onError: (error) => setResult({ severity: 'error', message: testErrorMessage(error) }),
  });

  return (
    <>
      <Button
        onClick={() => mutation.mutate()}
        disabled={disabled || mutation.isPending}
        startIcon={mutation.isPending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
      >
        Send test email
      </Button>
      <Snackbar
        open={!!result}
        autoHideDuration={5000}
        onClose={() => setResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {result ? (
          <Alert severity={result.severity} onClose={() => setResult(null)} variant="filled">
            {result.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
